import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1e293b'
  },
  headerBanner: {
    backgroundColor: '#115e59',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#99f6e4',
    marginTop: 2
  },
  badge: {
    backgroundColor: '#0d9488',
    color: '#ffffff',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 7.5,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  grid3: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#115e59',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.2,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 6.8,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 6.8,
    color: '#0f172a',
    width: '55%'
  },
  passText: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  failText: {
    color: '#b91c1c',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  signatureImage: {
    height: 32,
    maxWidth: 150,
    objectFit: 'contain',
    marginVertical: 2
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 5.8,
    color: '#64748b'
  }
});

const GroundSourceHeatPumpServiceMaintenancePDF = ({ formData = {} }) => {
  const business = formData.business || {};
  const job = formData.jobAddress || {};
  const landlord = formData.landlordAgent || {};
  const vessel = formData.vesselDetails || {};
  const heatPump = formData.heatPump || {};
  const groundLoop = formData.groundLoop || {};
  const operating = formData.operatingDetails || {};
  const defects = formData.defects || {};
  const work = formData.workCarriedOut || {};
  const issued = formData.issuedBy || {};
  const received = formData.receivedBy || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Banner */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Ground Source Heat Pump & Cylinder Service Checklist</Text>
            <Text style={styles.headerSubtitle}>Ground Loop (Brine Circuit) & Unvented Cylinder Maintenance Record</Text>
          </View>
          <View style={styles.badge}>
            <Text>GSHP SERVICE</Text>
          </View>
        </View>

        {/* Addresses */}
        <View style={styles.grid3}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. Service Company</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Business:</Text>
              <Text style={styles.fieldValue}>{business.businessName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{business.businessAddress || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tel:</Text>
              <Text style={styles.fieldValue}>{business.businessTel || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Site Address</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Name:</Text>
              <Text style={styles.fieldValue}>{job.jobName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{job.jobAddress || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tel:</Text>
              <Text style={styles.fieldValue}>{job.jobTel || 'N/A'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>3. Client / Landlord</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Name:</Text>
              <Text style={styles.fieldValue}>{landlord.landlordName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{landlord.landlordAddress || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tel:</Text>
              <Text style={styles.fieldValue}>{landlord.landlordTel || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* GSHP & Ground Loop Details */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>4. GSHP Unit Specification</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Make & Model:</Text>
              <Text style={styles.fieldValue}>{heatPump.make || 'N/A'} {heatPump.model || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Serial No.:</Text>
              <Text style={styles.fieldValue}>{heatPump.serialNumber || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Compressor Running Current:</Text>
              <Text style={styles.fieldValue}>{heatPump.compressorCurrentAmps ? `${heatPump.compressorCurrentAmps} A` : 'Normal'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Sight Glass / Moisture:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{heatPump.sightGlassClear || 'Clear / Dry (Green)'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>5. Ground Loop (Brine Circuit)</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Loop Collector Type:</Text>
              <Text style={styles.fieldValue}>{groundLoop.collectorType || 'Borehole / Horizontal Slinky'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Brine Pressure / Charge:</Text>
              <Text style={styles.fieldValue}>{groundLoop.brinePressureBar || 1.8} Bar</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Glycol Freeze Protection:</Text>
              <Text style={styles.fieldValue}>{groundLoop.glycolFreezePoint || '-15°C (30% Propylene Glycol)'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Brine Flow & Return ΔT:</Text>
              <Text style={styles.fieldValue}>{groundLoop.brineDeltaTC ? `${groundLoop.brineDeltaTC} °C` : '3.2 °C'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Ground Loop Filter / Strainer:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{groundLoop.strainerCleaned || 'Cleaned & Checked'}</Text>
            </View>
          </View>
        </View>

        {/* Operating Pressures & Cylinder */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>6. Heating Circuit & G3 Safety Valves</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Heating System Pressure:</Text>
              <Text style={styles.fieldValue}>{operating.systemOperatingPressure || 1.5} Bar</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Heating Vessel Pre-charge:</Text>
              <Text style={styles.fieldValue}>{operating.operatingPressureExpansionVessel || 1.5} Bar</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Safety PRV Lift Test:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{operating.prvLiftTest || 'Pass / Operating Correctly'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Tundish Discharge Line:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{operating.dischargeCompliant || 'Compliant (Continuous fall)'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>7. Hot Water Storage Cylinder</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Cylinder Make & Model:</Text>
              <Text style={styles.fieldValue}>{vessel.make || 'N/A'} {vessel.model || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Capacity & Location:</Text>
              <Text style={styles.fieldValue}>{vessel.capacity || '250'} L ({vessel.location || 'Plant Room'})</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Immersion Heater Tested:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{vessel.immersionTested || 'Yes / Operating'}</Text>
            </View>
          </View>
        </View>

        {/* Defects & Work Done */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>8. Defects Noted</Text>
            <Text style={{ fontSize: 6.5, color: defects.defectsIdentified ? '#b91c1c' : '#475569' }}>
              {defects.defectsIdentified || 'No defects recorded. Ground loop brine pressures and heat pump operating within nominal limits.'}
            </Text>
          </View>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>9. Work Carried Out</Text>
            <Text style={{ fontSize: 6.5, color: '#0f172a' }}>
              {work.workCarriedOut || 'Annual service completed: ground loop brine filters cleaned, glycol refractometer test verified, heating expansion vessels checked.'}
            </Text>
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>GSHP Service Engineer</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{issued.issuedByName || 'Accredited Renewables Engineer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>GSHP Service Stamp</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{issued.issuedByDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Customer / Client Receipt</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{received.receivedByName || 'Client / Duty Holder'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Maintenance record confirmed</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{received.receivedByDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Ground Source Heat Pump & Ground Loop Brine Annual Service Record
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default GroundSourceHeatPumpServiceMaintenancePDF;
